<script lang="ts">
	import type { ProductDetailProps } from "./ProductDetailProps.js";
	import Layout from "../components/Layout.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import ProductMobileGallery from "../components/Product/ProductMobileGallery.svelte";
	import { getContext, setContext } from "svelte";
	import { descriptionContextKey } from "../contexts/descriptionContextKey.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import ProductDesktopGallery from "../components/Product/ProductDesktopGallery.svelte";
	import ProductAllergens from "../components/Product/ProductAllergens.svelte";
	import ProductCartForm from "../components/Product/ProductCartForm.svelte";
	import Link from "../components/Link.svelte";
	import type { RegExpWithGroups } from "../../../core/utils/RegExpWithGroups.js";
	import ProductStructuredData from "../components/Product/ProductStructuredData.svelte";
	import WeddingsBanner from "../components/Wedding/WeddingsBanner.svelte";
	import Branches from "../components/Branches.svelte";
	import RelatedProducts from "../components/Product/RelatedProducts.svelte";
	import logo from "../../../core/assets/logo.png";
	import ProductPriorityLabel from "../components/Product/ProductPriorityLabel.svelte";
	import ProductFiltrationLabel from "../components/Product/ProductFiltrationLabel.svelte";
	import Content from "../components/Content.svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import DynamicPicture from "../components/Picture/DynamicPicture.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import { appName } from "../../../core/schema/appName.js";

	type $$Props = ProductDetailProps;

	const { product, productCategories, products } = $$restProps as $$Props;

	const { TENANT_LOCALE, STORAGE_URL } = getGlobalSettings();

	const [firstImage] = product.images;
	setContext(thumbnailImageContextKey, firstImage ? `${STORAGE_URL}/${firstImage.path}` : logo);

	const description = getContext<string>(descriptionContextKey);

	// prettier-ignore
	const firstParagraphRegex = (/(?<wholeParagraph><p[^>]*>(?<content>.*?)<\/p>)/u) as RegExpWithGroups<{
		content: string;
		wholeParagraph: string;
	}>;
	$: firstParagraph = firstParagraphRegex.exec(product.description[TENANT_LOCALE]);
	$: shortDescription = firstParagraph?.groups.content.trim() ?? "";
	$: longDescription = product.description[TENANT_LOCALE].replace(
		firstParagraph?.groups.wholeParagraph ?? "",
		"",
	).replace(/^(?:<p>(?:\s|&nbsp;)*<\/p>)+/u, "");
	$: readMore = longDescription.length > 0;
	// TODO SD-219
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	$: firstCategory = productCategories[0]!;

	// TODO Remove this after Svelte + ESLint are better friends https://github.com/sveltejs/svelte-eslint-parser/issues/306#issuecomment-1938748431
	/* eslint-disable @typescript-eslint/no-unsafe-argument, svelte/no-at-html-tags */
</script>

<ProductStructuredData {product} productCategory={firstCategory} {description} />

<Layout>
	<section>
		<div class="container">
			<h1 class="mb-8 mt-5 px-4 py-0 text-[1.5rem] font-semibold leading-8 md:!hidden md:text-xl md:leading-[3.25rem]">
				{nbspify(product.name[TENANT_LOCALE])}
			</h1>
		</div>
		{#if product.images.length === 0}
			<div class="container md:hidden">
				<div
					class="bg-secondaryBlack-40 flex aspect-square h-auto max-h-[37rem] w-full max-w-[37rem] items-center justify-center rounded-md"
				>
					<StaticPicture
						alt="Logo {appName}"
						image={logo}
						class="w-28 opacity-50 sm:w-36"
						loading="eager"
						width={768}
						height={768}
					/>
				</div>
			</div>
		{:else if product.images.length > 1}
			<ProductMobileGallery {product} images={product.images} class="mb-8 md:hidden" />
		{:else}
			{@const [image] = product.images}
			{#if image}
				<div class="container md:hidden">
					<DynamicPicture
						imgClass="rounded-md"
						{image}
						width={768}
						height={768}
						alt="Fotografie – {product.name[TENANT_LOCALE]}"
						lightboxId={product.id}
						lightboxWidth={800}
						bucket="storage"
						loading="eager"
					/>
				</div>
			{/if}
		{/if}
		<div class="container">
			<div class="mt-4 flex flex-col gap-28">
				<div class="flex flex-col md:grid md:grid-cols-2 md:gap-10">
					<ProductDesktopGallery {product} images={product.images} class="hidden md:block" />

					<div>
						<div class="mb-8 flex flex-col items-start gap-3 md:gap-4">
							{#if product.priorityLabel !== undefined}
								<ProductPriorityLabel class="text-xs font-medium" label={product.priorityLabel} withoutBackground />
							{/if}

							<h1
								class="m-0 hidden p-0 text-[1.5rem] font-semibold leading-8 md:inline-block md:text-xl md:leading-[3.25rem]"
							>
								{product.name[TENANT_LOCALE]}
							</h1>

							{#if product.filtrationLabels[TENANT_LOCALE].length > 0}
								<div class="flex flex-wrap items-center gap-1.5">
									{#each product.filtrationLabels[TENANT_LOCALE] as label}
										<ProductFiltrationLabel {label} />
									{/each}
								</div>
							{/if}
						</div>

						<div class="mt-6">
							<Content content={shortDescription} />
							{#if readMore}
								<Link href="#longDescription">Chci vědět více</Link>
							{/if}
						</div>

						<ProductAllergens allergens={product.allergens} />

						<ProductCartForm {product} />
					</div>
				</div>

				<div id="longDescription">
					<Content content={longDescription} />
				</div>
			</div>
		</div>
	</section>

	<div class="container">
		<section>
			<RelatedProducts headline="Mohlo by vás zajímat" {products} category={firstCategory.name[TENANT_LOCALE]} />
		</section>

		<section>
			<WeddingsBanner />
		</section>

		<section>
			<Branches />
		</section>
	</div>
</Layout>
